import type {
  IActiveAnnouncement,
  IAnnouncementContent,
} from '@/apiClient/types/announcements'
import { DEFAULT_ANNOUNCEMENT_BUTTON_LABEL } from '@/lib/announcements/messageTypes'
import { richTextToPlainText, sanitizeRichText } from '@/utils/richText'
import {
  getBlockText,
  parseRichText,
} from '@/views/components/ui/editor/richTextModel'

import {
  ANNOUNCEMENT_HEADER_ILLUSTRATIONS,
  getHeaderIllustration,
} from './headerIllustrations'

import type { AnnouncementModalConfig } from './announcementModals.config'
import type { IIcons } from '@foundever/ui-lib'

/**
 * One shape the modal can render, whatever produced it — an admin-published
 * announcement or the built-in fallback config. Keeping the component free of
 * that branch is what lets the two sources look identical on screen.
 */
export interface AnnouncementModalView {
  /** Queue key. Namespaced per source so the two can never collide. */
  id: string
  ariaLabel: string
  /** Rendered above the body; empty for announcements, whose heading is in the HTML. */
  title: string
  /** Sanitised HTML, safe for `v-html`. */
  bodyHtml: string
  /**
   * Header artwork, passed straight to `UIModal`'s `headerImageSrc`. Always set:
   * every announcement carries a header, and its presence is also what makes the
   * dialog acknowledge-only — ui-lib drops the close control, Escape and the
   * backdrop when a header image is given.
   */
  imageSrc: string
  imageAlt: string
  buttonLabel: string
  buttonIcon?: IIcons
  /** `null` hides the reminder checkbox. */
  reminderLabel: string | null
}

/**
 * What a message type with no illustration of its own falls back to, so the
 * panel never opens with a bare strip where the artwork should be. Only
 * reachable through `custom`, which is the one type carrying no illustration.
 */
const FALLBACK_ILLUSTRATION = ANNOUNCEMENT_HEADER_ILLUSTRATIONS.info ?? ''

/**
 * First heading in the body, used as the dialog's accessible name. Takes the
 * *sanitised* body, so the name always describes what is actually on screen.
 */
const getHeadline = (bodyHtml: string): string => {
  const doc = parseRichText(bodyHtml)
  const heading = doc.find((block) => block.type === 'h3')
  if (heading) return getBlockText(heading)
  return richTextToPlainText(bodyHtml).slice(0, 120)
}

/**
 * The editable half on its own, so the admin live preview can render exactly
 * what a published announcement would look like from an unsaved draft.
 */
export const contentToView = (
  content: IAnnouncementContent,
  id: string,
): AnnouncementModalView => {
  const customImage =
    content.message_type === 'custom'
      ? (content.header_image_url?.trim() ?? '')
      : ''

  const bodyHtml = sanitizeRichText(content.body_html)

  return {
    id,
    ariaLabel: getHeadline(bodyHtml) || 'Announcement',
    title: '',
    bodyHtml,
    imageSrc:
      customImage ||
      getHeaderIllustration(content.message_type) ||
      FALLBACK_ILLUSTRATION,
    // Our own illustrations are decorative; only an admin's image could carry
    // copy, and nothing collects alt text for it today.
    imageAlt: '',
    buttonLabel:
      content.button_label?.trim() || DEFAULT_ANNOUNCEMENT_BUTTON_LABEL,
    reminderLabel: content.is_dismissal_remembered
      ? "Don't remind me again"
      : null,
  }
}

export const announcementToView = (
  announcement: IActiveAnnouncement,
): AnnouncementModalView =>
  contentToView(announcement, `announcement:${announcement.id}`)

export const configToView = (
  config: AnnouncementModalConfig,
): AnnouncementModalView => ({
  id: `config:${config.id}`,
  ariaLabel: config.ariaLabel,
  title: config.title,
  bodyHtml: sanitizeRichText(
    config.paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join(''),
  ),
  imageSrc: config.image,
  imageAlt: config.imageAlt,
  buttonLabel: config.primaryButton.label,
  buttonIcon: config.primaryButton.icon,
  reminderLabel: config.reminder?.checkboxLabel ?? null,
})
